import { get } from 'lodash';
import { redirect } from 'redux-first-router';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import {
  ACTION_REQUEST_LOGIN,
  ACTION_REQUEST_LOGIN_SOCIAL,
  ACTION_REQUEST_SIGNUP,
  FETCH_USER_FAILURED,
  FETCH_USER_SUCCESS,
  forgotPasswordAPI,
  loginAPI,
  loginSocialAPI,
  LOGOUT_ACTION,
  REQUEST_FORGOT_PASSWORD_ACTION,
  REQUEST_FORGOT_PASSWORD_FAILURE,
  REQUEST_FORGOT_PASSWORD_SUCCESS,
  REQUEST_LOGIN_FAILURE,
  REQUEST_LOGIN_SOCIAL_SUCCESS,
  REQUEST_LOGIN_SUCCESS,
  REQUEST_SIGNUP_FAILURE,
  REQUEST_SIGNUP_SUCCESS,
  REQUEST_UNLINK_SOCIAL_ACTION,
  REQUEST_UNLINK_SOCIAL_FAILURE,
  REQUEST_UNLINK_SOCIAL_SUCCESS,
  REQUEST_VERIFY_FORGOT_PASSWORD_ACTION,
  REQUEST_VERIFY_FORGOT_PASSWORD_SUCCESS,
  REQUEST_VERIFY_FORGOT_PASSWORD_FAILURE,
  verifyForgotPasswordOTPAPI,
  signupAPI,
  unlinkSocialAPI,
} from '.';
import { getErrorMessage } from '../../../utils/error';
import {
  getTemporaryAuthToken,
  removeAuthToken,
  removeRefCookie,
  removeTemporaryAuthToken,
  setAuthToken,
  setTemporaryAuthToken,
} from '../../../utils/localStorage';
import { bootFinished, showToastAlert } from '../app';
import {
  ROUTE_HOME,
  ROUTE_LOGIN,
  ROUTE_OTP_VERIFICATION,
  ROUTE_RESET_PASSWORD,
  ROUTE_SIGNUP,
  selectRoutesMap,
  selectRouteType,
} from '../routing';
import { getProfilesAPI } from '../profile';
import { resendOTPAPI } from '../otpVerification';
import { deleteFCMToken } from '../fcm';

function* redirectAwayAfterUserLogin() {
  yield takeLatest(FETCH_USER_SUCCESS, function* (_action) {
    const { location, auth } = yield select();
    const { isAllowRedirect } = auth;
    let redirectType = get(location, 'type', '');
    const payload = get(location, 'payload', '');
    const query = get(location, 'query', '');

    yield put(bootFinished());

    // Được phép đi tiếp
    const authRoutes = [ROUTE_LOGIN, ROUTE_SIGNUP, ROUTE_OTP_VERIFICATION];
    if (isAllowRedirect)
      yield put(
        redirect({
          type: redirectType && !authRoutes.includes(redirectType) ? redirectType : ROUTE_HOME,
          payload,
          query,
        })
      );
  });

  yield takeLatest(FETCH_USER_FAILURED, function* (_action) {
    const { location } = yield select();
    let redirectType = get(location, 'type', '');
    const payload = get(location, 'payload', '');
    const query = get(location, 'query', '');

    yield put(bootFinished());

    const routesMap = yield select(selectRoutesMap);
    const routeType = yield select(selectRouteType);
    const currentRoute = routesMap[routeType];
    if (currentRoute.requiresAuth) {
      removeAuthToken();
      yield put(
        redirect({
          type: redirectType && redirectType !== ROUTE_LOGIN ? ROUTE_LOGIN : redirectType,
          payload,
          query,
        })
      );
    }
  });
}

function* login() {
  yield takeLatest([ACTION_REQUEST_LOGIN_SOCIAL], function* (action) {
    removeTemporaryAuthToken();
    removeAuthToken();
    yield put(loginSocialAPI(action.payload));
  });

  yield takeLatest([ACTION_REQUEST_LOGIN], function* (action) {
    removeAuthToken();
    yield put(loginAPI(action.payload));
  });

  yield takeLatest([REQUEST_LOGIN_SUCCESS], function* (action) {
    const { data } = action.payload;
    const { token, user } = data;
    // check if user verify email
    if (user && !user.emailVerified) {
      // set temporary auth token
      setTemporaryAuthToken(token);
      yield put(
        redirect({
          type: ROUTE_OTP_VERIFICATION,
        })
      );
      yield put(showToastAlert('Please verify your email!', 'error'));
      return;
    }

    setAuthToken(token);
    // remove temporary auth token
    removeTemporaryAuthToken();
    yield put(
      redirect({
        type: ROUTE_HOME,
      })
    );
    yield put(showToastAlert('Login successfully!', 'success'));
  });

  yield takeLatest([REQUEST_LOGIN_SOCIAL_SUCCESS], function* (action) {
    const { data } = action.payload;
    const { token } = data;

    setAuthToken(token);
    // remove temporary auth token
    removeTemporaryAuthToken();
    // remove ref cookie after successful social login
    removeRefCookie();
    yield put(
      redirect({
        type: ROUTE_HOME,
      })
    );
    yield put(showToastAlert('Login successfully!', 'success'));
  });

  yield takeLatest([REQUEST_LOGIN_FAILURE], function* (action) {
    const errorCode = action?.payload?.data?.error;
    const messageError = action?.payload?.data?.message;
    const isHiddenToast = action?.payload?.data?.isHiddenToast;

    // Check if account is not verified
    if (errorCode === 'AccountNotVerified') {
      // Try to get token from temporary storage first (from previous signup)
      let temporaryToken = getTemporaryAuthToken();

      // If no temporary token, try to get from error response
      if (!temporaryToken) {
        temporaryToken = action?.payload?.data?.token || action?.payload?.data?.data;
      }

      // If still no token, try to get token by calling resend OTP API
      if (!temporaryToken) {
        const email = action?.meta?.variables?.email;
        if (email) {
          try {
            // Call resend OTP API (thunk) - need dispatch and getState
            const state = yield select();
            const getState = () => state;
            const dispatch = (action) => put(action);

            // resendOTPAPI returns a thunk function, call it with dispatch and getState
            const thunk = resendOTPAPI({ email });
            const response = yield call(thunk, dispatch, getState);

            // Check if resend API returns token
            temporaryToken =
              response?.data?.token ||
              response?.data ||
              response?.token;
          } catch (error) {
            // If resend fails, log error but continue
            console.error('Failed to resend OTP to get token:', error);
          }
        }
      }

      // Get email from login request
      const email = action?.meta?.variables?.email;

      // If we have a token, save it and redirect to OTP verification
      if (temporaryToken) {
        setTemporaryAuthToken(temporaryToken);
        yield put(
          redirect({
            type: ROUTE_OTP_VERIFICATION,
            query: email ? { email } : {},
          })
        );
        yield put(
          showToastAlert(
            messageError || 'Your account is not verified. Please check your email and enter the verification code.',
            'error'
          )
        );
        return;
      } else {
        // If no token, still redirect to OTP verification
        // User can resend OTP from there
        yield put(
          redirect({
            type: ROUTE_OTP_VERIFICATION,
            query: email ? { email } : {},
          })
        );
        yield put(
          showToastAlert(
            messageError || 'Your account is not verified. Please check your email and enter the verification code.',
            'error'
          )
        );
        return;
      }
    }

    if (!isHiddenToast) {
      yield put(
        showToastAlert(messageError || action?.error?.message || 'Failed to login!', 'error')
      );
    }
  });
}

function* signup() {
  yield takeLatest([ACTION_REQUEST_SIGNUP], function* (action) {
    removeAuthToken();
    removeTemporaryAuthToken();
    yield put(signupAPI(action.payload));
  });

  yield takeLatest([REQUEST_SIGNUP_SUCCESS], function* (action) {
    const temporaryToken = action?.payload?.data?.token || action?.payload?.data;
    // Get email from signup request
    const email = action?.meta?.variables?.email;

    if (temporaryToken) {
      setTemporaryAuthToken(temporaryToken);
      yield put(
        redirect({
          type: ROUTE_OTP_VERIFICATION,
          query: email ? { email } : {},
        })
      );
    }
    yield put(showToastAlert('Register successfully!', 'success'));
  });

  yield takeLatest([REQUEST_SIGNUP_FAILURE], function* (action) {
    const messageError = action?.payload?.data?.message;
    const isHiddenToast = action?.payload?.data?.isHiddenToast;
    if (!isHiddenToast) {
      yield put(
        showToastAlert(messageError || action?.error?.message || 'Failed to register!', 'error')
      );
    }
  });
}

function* logout() {
  removeAuthToken();
  const { location } = yield select();
  if (import.meta.REACT_APP_ENV === 'local') {
    yield put(
      redirect({
        type: ROUTE_LOGIN,
        query: location.type !== ROUTE_LOGIN ? { redirect: location.type } : {},
      })
    );
  } else {
    window.location.pathname = '/login';
  }
}

function* forgotPassword() {
  yield takeLatest([REQUEST_FORGOT_PASSWORD_ACTION], function* (action) {
    yield put(forgotPasswordAPI(action.payload));
  });

  yield takeLatest([REQUEST_FORGOT_PASSWORD_SUCCESS], function* () {
    yield put(showToastAlert('Forgot Password sent email successfully!', 'success'));
  });

  yield takeLatest([REQUEST_FORGOT_PASSWORD_FAILURE], function* (action) {
    yield put(showToastAlert(getErrorMessage(action.payload) || 'Failed to send email!', 'error'));
  });
}

function* handleUnlinkSocial() {
  yield takeLatest([REQUEST_UNLINK_SOCIAL_ACTION], function* (action) {
    yield put(unlinkSocialAPI(action.payload));
  });
  yield takeLatest([REQUEST_UNLINK_SOCIAL_SUCCESS], function* () {
    yield put(getProfilesAPI());
    yield put(showToastAlert('Unlink social successfully!', 'success'));
  });
  yield takeLatest([REQUEST_UNLINK_SOCIAL_FAILURE], function* (action) {
    yield put(
      showToastAlert(getErrorMessage(action.payload) || 'Failed to unlink social!', 'error')
    );
  });
}

function* verifyForgotPasswordOTP() {
  yield takeLatest([REQUEST_VERIFY_FORGOT_PASSWORD_ACTION], function* (action) {
    yield put(verifyForgotPasswordOTPAPI(action.payload));
  });

  yield takeLatest([REQUEST_VERIFY_FORGOT_PASSWORD_SUCCESS], function* (action) {
    const resetToken = action?.payload?.data;

    if (resetToken) {

      yield put(showToastAlert('OTP verified successfully! Redirecting to set new password.', 'success'));

      yield put(
        redirect({
          type: ROUTE_RESET_PASSWORD,
          query: {
            token: resetToken
          }
        })
      );

    } else {
      yield put(showToastAlert('Verification succeeded, but token was missing.', 'error'));
    }
  });

  yield takeLatest([REQUEST_VERIFY_FORGOT_PASSWORD_FAILURE], function* (action) {
    yield put(showToastAlert(getErrorMessage(action.payload) || 'Invalid OTP code!', 'error'));
  });
}

export default function* auth() {
  yield all([
    takeLatest(LOGOUT_ACTION, logout),
    fork(redirectAwayAfterUserLogin),
    fork(login),
    fork(forgotPassword),
    fork(signup),
    fork(handleUnlinkSocial),
    fork(verifyForgotPasswordOTP),
  ]);
}